import React, { useEffect, useMemo, useRef, useState } from "react"
import { useLanguage } from "../context/LanguageContext"
import { listCountries, localeForLanguage, countryName } from "../data/countries"
import CountryFlag from "./CountryFlag"

export default function CountrySelect({ id, value, onChange, placeholder }) {
  const { t, language } = useLanguage()
  const locale = localeForLanguage(language)
  const rootRef = useRef(null)
  const searchRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")

  const countries = useMemo(() => listCountries(locale), [locale])
  const selectedName = value ? countryName(value, locale) : ""
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return countries
    return countries.filter(
      (country) =>
        country.name.toLowerCase().includes(needle) ||
        country.code.toLowerCase().includes(needle)
    )
  }, [countries, query])

  useEffect(() => {
    if (!open) return undefined
    searchRef.current?.focus()
    const onPointer = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false)
        setQuery("")
      }
    }
    const onKey = (event) => {
      if (event.key === "Escape") {
        setOpen(false)
        setQuery("")
      }
    }
    document.addEventListener("mousedown", onPointer)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", onPointer)
      document.removeEventListener("keydown", onKey)
    }
  }, [open])

  const choose = (code) => {
    onChange(code)
    setOpen(false)
    setQuery("")
  }

  return (
    <div className="country-select" ref={rootRef}>
      <button
        type="button"
        id={id}
        className="profile-input country-select-trigger"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((next) => !next)}
      >
        {value ? (
          <span className="country-select-value">
            <CountryFlag code={value} />
            <span>{selectedName}</span>
          </span>
        ) : (
          <span className="country-select-placeholder">{placeholder}</span>
        )}
        <span className="country-select-caret" aria-hidden>
          ▾
        </span>
      </button>
      {open && (
        <div className="country-select-panel">
          <input
            ref={searchRef}
            type="search"
            className="country-select-search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("searchCountry")}
            aria-label={t("searchCountry")}
          />
          <ul className="country-select-list" role="listbox">
            <li>
              <button type="button" className="country-select-option" onClick={() => choose("")}>
                {t("clearCountry")}
              </button>
            </li>
            {filtered.map((country) => (
              <li key={country.code}>
                <button
                  type="button"
                  role="option"
                  aria-selected={country.code === value}
                  className={`country-select-option${country.code === value ? " is-selected" : ""}`}
                  onClick={() => choose(country.code)}
                >
                  <CountryFlag code={country.code} />
                  <span>{country.name}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
